import { StatsigEnvironment } from "@statsig/js-client";
import { createContext, ReactNode, useContext } from "react";
import {
  StatsigProvider,
  useClientAsyncInit,
  useStatsigUser,
} from "@statsig/react-bindings";

const STATSIG_CLIENT_KEY = process.env.REACT_APP_STATSIG_CLIENT_KEY || "";

interface FayStatsigContextValue {
  setUserFromProviderId: (providerId: string, isAdmin: boolean) => void;
  setUserFromPatientId: (patientId: string) => void;
  setUserToNull: () => void;
}

const FayStatsigContext = createContext<FayStatsigContextValue>({
  setUserFromProviderId: (providerId: string, isAdmin: boolean) => {},
  setUserFromPatientId: (patientId: string) => {},
  setUserToNull: () => {},
});

const makeUserId = (userId: string | null, userType: "patient" | "provider") =>
  userId ? `${userType}:${userId}` : undefined;
const ENVIRONMENT = process.env.REACT_APP_ENVIRONMENT;

const getStatsigEnvironment = (): StatsigEnvironment => {
  return {
    tier: ENVIRONMENT === "production" ? "production" : "development",
  };
};

const NULL_USER = {
  userID: "provider:placeholder",
  custom: { isAdmin: false },
};

export const FayStatsigProviderInner = ({
  children,
}: {
  children: ReactNode;
}) => {
  const { updateUserAsync } = useStatsigUser();

  const setUserFromProviderId = async (
    providerId: string,
    isAdmin: boolean,
  ) => {
    const userId = makeUserId(providerId, "provider");
    await updateUserAsync({
      userID: userId || "provider:placeholder",
      custom: { isAdmin },
    });
  };

  const setUserFromPatientId = async (patientId: string) => {
    const userId = makeUserId(patientId, "patient");
    await updateUserAsync({
      userID: userId || "provider:placeholder",
      custom: { isAdmin: false },
    });
  };

  const setUserToNull = async () => {
    await updateUserAsync(NULL_USER);
  };

  // NOTE: These functions should not be dependencies in a useEffect as they
  // are subject to change from the upstream updateUserAsync calls.
  const context = {
    setUserFromProviderId,
    setUserFromPatientId,
    setUserToNull,
  };

  return (
    <FayStatsigContext.Provider value={context}>
      {children}
    </FayStatsigContext.Provider>
  );
};

export const FayStatsigProvider = ({ children }: { children: ReactNode }) => {
  const { client } = useClientAsyncInit(STATSIG_CLIENT_KEY, NULL_USER, {
    environment: getStatsigEnvironment(),
  });

  return (
    <StatsigProvider client={client}>
      <FayStatsigProviderInner>{children}</FayStatsigProviderInner>
    </StatsigProvider>
  );
};

export function useFayStatsig() {
  return useContext(FayStatsigContext);
}
